<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col">
        <button class="btn btn-link text-muted px-0 mb-2" (click)="goBack()">
          <i class="bi bi-arrow-left me-1"></i>
          {{ isEditMode ? 'Back to Test Details' : 'Back to Safety Tests' }}
        </button>
        <h1 class="h2 mb-1 fw-bold">{{ isEditMode ? 'Edit Test' : 'Create Test' }}</h1>
      </div>
    </div>

    <!-- Error/Success Messages -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''"></button>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Form -->
    <form *ngIf="!loading" [formGroup]="form" (ngSubmit)="save()">
      <div class="row g-4">
        <!-- Left Column: Test Details -->
        <div class="col-lg-5">
          <div class="card sticky-top" style="top: 1rem;">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Test Details</h5>
            </div>
            <div class="card-body">
              <div class="mb-3">
                <label class="form-label">Title <span class="text-danger">*</span></label>
                <input type="text" class="form-control" formControlName="title"
                       placeholder="e.g., Agricultural Drone Safety Assessment">
              </div>

              <div class="mb-3">
                <label class="form-label">Short Name</label>
                <input type="text" class="form-control" formControlName="shortName"
                       placeholder="e.g., ADSA">
              </div>

              <div class="mb-3">
                <label class="form-label">Description</label>
                <textarea class="form-control" formControlName="description" rows="3"
                          placeholder="Brief description of the test..."></textarea>
              </div>

              <div class="mb-3">
                <label class="form-label">Category</label>
                <select class="form-select" formControlName="categoryId">
                  <option value="">No Category</option>
                  <option *ngFor="let cat of categories" [value]="cat.id">{{ cat.name }}</option>
                </select>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Passing Score (%) <span class="text-danger">*</span></label>
                  <input type="number" class="form-control" formControlName="passingScore"
                         min="1" max="100">
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Time Limit (minutes)</label>
                  <input type="number" class="form-control" formControlName="timeLimit"
                         min="1" placeholder="No limit">
                </div>
              </div>

              <div class="mb-3">
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" id="shuffleQuestions"
                         formControlName="shuffleQuestions">
                  <label class="form-check-label" for="shuffleQuestions">
                    Shuffle question order for each attempt
                  </label>
                </div>
              </div>

              <div class="mb-3">
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" id="shuffleOptions"
                         formControlName="shuffleOptions">
                  <label class="form-check-label" for="shuffleOptions">
                    Shuffle answer options for each question
                  </label>
                </div>
              </div>

              <div *ngIf="isEditMode" class="mb-3">
                <label class="form-label">Version Notes</label>
                <textarea class="form-control" formControlName="versionNotes" rows="2"
                          placeholder="Notes about this version..."></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Questions -->
        <div class="col-lg-7">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">
                <i class="bi bi-list-ol me-2"></i>Questions ({{ questions.length }})
              </h5>
              <div class="d-flex gap-2">
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="exportQuestions()"
                        *ngIf="questions.length > 0" title="Export questions to CSV">
                  <i class="bi bi-download me-1"></i>Export CSV
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="openImportModal()">
                  <i class="bi bi-upload me-1"></i>Import CSV
                </button>
                <button type="button" class="btn btn-sm btn-outline-primary" (click)="addQuestion()">
                  <i class="bi bi-plus-circle me-1"></i>Add Question
                </button>
              </div>
            </div>
            <div class="card-body p-0">
              <div *ngIf="questions.length === 0" class="text-center text-muted py-5">
                <i class="bi bi-question-circle fs-1 mb-3 d-block"></i>
                <p>No questions yet. Click "Add Question" to get started.</p>
              </div>

              <div class="list-group list-group-flush">
                <div *ngFor="let question of questions; let i = index"
                     class="question-item list-group-item bg-transparent p-0"
                     [class.opacity-50]="draggedQuestionIndex === i"
                     [class.invalid]="!isQuestionValid(question)"
                     draggable="true"
                     (dragstart)="onDragStart(i)"
                     (dragover)="onDragOver($event, i)"
                     (drop)="onDrop($event, i)"
                     (dragend)="onDragEnd()">

                  <!-- Question Header -->
                  <div class="question-header d-flex align-items-center gap-2 p-3 border-bottom"
                       [class.border-bottom-0]="!question.expanded">
                    <div class="drag-handle text-muted">
                      <i class="bi bi-grip-vertical"></i>
                    </div>
                    <span class="badge bg-light text-dark">{{ i + 1 }}</span>
                    <span class="badge" [class.bg-secondary]="question.type === 'multiple_choice'"
                          [class.bg-info]="question.type === 'true_false'">
                      {{ question.type === 'true_false' ? 'T/F' : 'MC' }}
                    </span>
                    <div class="flex-grow-1 text-truncate cursor-pointer" (click)="toggleQuestion(i)">
                      {{ question.text || 'New Question' }}
                    </div>
                    <div class="d-flex gap-1">
                      <button type="button" class="btn btn-sm btn-link text-muted p-1"
                              (click)="moveQuestionUp(i)" [disabled]="i === 0" title="Move up">
                        <i class="bi bi-chevron-up"></i>
                      </button>
                      <button type="button" class="btn btn-sm btn-link text-muted p-1"
                              (click)="moveQuestionDown(i)" [disabled]="i === questions.length - 1" title="Move down">
                        <i class="bi bi-chevron-down"></i>
                      </button>
                      <button type="button" class="btn btn-sm btn-link text-muted p-1"
                              (click)="duplicateQuestion(i)" title="Duplicate">
                        <i class="bi bi-copy"></i>
                      </button>
                      <button type="button" class="btn btn-sm btn-link text-danger p-1"
                              (click)="removeQuestion(i)" title="Delete">
                        <i class="bi bi-trash"></i>
                      </button>
                      <button type="button" class="btn btn-sm btn-link text-muted p-1"
                              (click)="toggleQuestion(i)">
                        <i class="bi" [class.bi-chevron-down]="!question.expanded"
                           [class.bi-chevron-up]="question.expanded"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Question Body -->
                  <div class="p-3" *ngIf="question.expanded">
                    <div class="row g-3">
                      <div class="col-md-8">
                        <label class="form-label">Question Text <span class="text-danger">*</span></label>
                        <textarea class="form-control" rows="2" [(ngModel)]="question.text"
                                  [ngModelOptions]="{standalone: true}"
                                  placeholder="Enter your question..."></textarea>
                      </div>
                      <div class="col-md-4">
                        <label class="form-label">Type</label>
                        <select class="form-select" [(ngModel)]="question.type"
                                [ngModelOptions]="{standalone: true}"
                                (change)="onQuestionTypeChange(question)">
                          <option value="multiple_choice">Multiple Choice</option>
                          <option value="true_false">True/False</option>
                        </select>
                      </div>
                      <div class="col-12">
                        <label class="form-label">Explanation (shown after answering)</label>
                        <textarea class="form-control" rows="2" [(ngModel)]="question.explanation"
                                  [ngModelOptions]="{standalone: true}"
                                  placeholder="Optional explanation for the correct answer..."></textarea>
                      </div>
                    </div>

                    <!-- Options -->
                    <div class="mt-3">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <label class="form-label mb-0">Answer Options</label>
                        <button type="button" class="btn btn-sm btn-outline-secondary"
                                *ngIf="question.type !== 'true_false' && question.options.length < 6"
                                (click)="addOption(question)">
                          <i class="bi bi-plus me-1"></i>Add Option
                        </button>
                      </div>
                      <div class="options-list">
                        <div *ngFor="let option of question.options; let j = index"
                             class="d-flex align-items-center gap-2 mb-2 py-2 px-3 rounded border"
                             [class.bg-success-subtle]="option.isCorrect"
                             [class.border-success-subtle]="option.isCorrect">
                          <div class="form-check fs-5 mb-0">
                            <input type="radio" class="form-check-input cursor-pointer"
                                   [name]="'correct_' + i"
                                   [checked]="option.isCorrect"
                                   (change)="setCorrectOption(question, j)"
                                   [disabled]="question.type === 'true_false'">
                          </div>
                          <span class="badge" [class.bg-success]="option.isCorrect" [class.bg-secondary]="!option.isCorrect">
                            {{ ['A', 'B', 'C', 'D', 'E', 'F'][j] }}
                          </span>
                          <input type="text" class="form-control form-control-sm flex-grow-1"
                                 [(ngModel)]="option.text"
                                 [ngModelOptions]="{standalone: true}"
                                 [disabled]="question.type === 'true_false'"
                                 placeholder="Option text...">
                          <button type="button" class="btn btn-sm btn-link text-danger p-1"
                                  *ngIf="question.type !== 'true_false' && question.options.length > 2"
                                  (click)="removeOption(question, j)" title="Remove option">
                            <i class="bi bi-x-lg"></i>
                          </button>
                        </div>
                      </div>
                      <small class="text-muted">
                        <i class="bi bi-info-circle me-1"></i>
                        Select the radio button next to the correct answer
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="row mt-4">
        <div class="col">
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <span *ngIf="!allQuestionsValid && questions.length > 0" class="text-warning">
                    <i class="bi bi-exclamation-triangle me-1"></i>
                    Some questions are incomplete
                  </span>
                  <span *ngIf="questions.length === 0" class="text-muted">
                    Add at least one question to activate the test
                  </span>
                </div>
                <div class="d-flex gap-2">
                  <button type="button" class="btn btn-outline-secondary" (click)="goBack()">
                    Cancel
                  </button>
                  <button type="button" class="btn btn-outline-primary" (click)="save(false)"
                          [disabled]="!canSave || saving">
                    <span *ngIf="saving" class="spinner-border spinner-border-sm me-1"></span>
                    Save as Draft
                  </button>
                  <button type="button" class="btn btn-success" (click)="save(true)"
                          [disabled]="!canActivate || saving"
                          *ngIf="!isEditMode">
                    <span *ngIf="saving" class="spinner-border spinner-border-sm me-1"></span>
                    Save & Activate
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</div>

<!-- Import CSV Modal -->
<div class="modal fade" id="importModal" tabindex="-1">
  <div class="modal-dialog modal-lg">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Import Questions from CSV</h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal"></button>
      </div>
      <div class="modal-body">
        <div class="mb-3">
          <label class="form-label">Select CSV File</label>
          <input type="file" class="form-control" accept=".csv" (change)="onFileSelected($event)">
          <small class="text-muted d-block mt-1">
            <a href="javascript:void(0)" (click)="downloadCsvTemplate()">
              <i class="bi bi-download me-1"></i>Download CSV template
            </a>
            <span class="mx-2">|</span>
            Expected format: Question Number, Question Text, Question Type, Explanation, Option A, Option A Correct, ...
          </small>
        </div>

        <!-- Replace Mode Toggle -->
        <div *ngIf="questions.length > 0" class="mb-3">
          <div class="form-check form-switch">
            <input type="checkbox" class="form-check-input" id="importReplaceMode"
                   [(ngModel)]="importReplaceMode" [ngModelOptions]="{standalone: true}">
            <label class="form-check-label" for="importReplaceMode">
              Replace existing questions
            </label>
          </div>
          <small class="text-muted d-block mt-1">
            <span *ngIf="importReplaceMode" class="text-warning">
              <i class="bi bi-exclamation-triangle me-1"></i>
              All {{ questions.length }} existing questions will be removed and replaced with imported questions.
            </span>
            <span *ngIf="!importReplaceMode">
              Imported questions will be added after the existing {{ questions.length }} questions.
            </span>
          </small>
        </div>

        <!-- Preview -->
        <div *ngIf="importPreview" class="mt-4">
          <h6>Preview</h6>
          <div class="d-flex gap-3 mb-3">
            <span class="badge bg-secondary">{{ importPreview.totalRows }} total rows</span>
            <span class="badge bg-success">{{ importPreview.validRows }} valid</span>
            <span class="badge bg-danger" *ngIf="importPreview.invalidRows > 0">
              {{ importPreview.invalidRows }} invalid
            </span>
          </div>

          <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
            <table class="table table-sm table-bordered">
              <thead>
                <tr>
                  <th style="width: 40px;">#</th>
                  <th>Question</th>
                  <th style="width: 80px;">Type</th>
                  <th style="width: 80px;">Options</th>
                  <th style="width: 60px;">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let q of importPreview.questions; let i = index"
                    [class.table-danger]="!q.isValid">
                  <td>{{ i + 1 }}</td>
                  <td>
                    <div class="text-truncate" style="max-width: 300px;">{{ q.text }}</div>
                    <small *ngIf="q.errors.length > 0" class="text-danger d-block">
                      {{ q.errors.join(', ') }}
                    </small>
                  </td>
                  <td>{{ q.type === 'true_false' ? 'T/F' : 'MC' }}</td>
                  <td>{{ q.options.length }}</td>
                  <td>
                    <i *ngIf="q.isValid" class="bi bi-check-circle text-success"></i>
                    <i *ngIf="!q.isValid" class="bi bi-x-circle text-danger"></i>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
        <button type="button" class="btn" [ngClass]="importReplaceMode ? 'btn-warning' : 'btn-primary'"
                (click)="confirmImport()"
                [disabled]="!importPreview || importPreview.validRows === 0 || importing">
          <span *ngIf="importing" class="spinner-border spinner-border-sm me-1"></span>
          {{ importReplaceMode ? 'Replace with' : 'Import' }} {{ importPreview?.validRows || 0 }} Questions
        </button>
      </div>
    </div>
  </div>
</div>
